import { AlertIcon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { formatNumber, guardNumber } from "@msuite/utils/numbers";
import type { OrderFormSchema } from "@msuite/utils/schemas";
import type { LoadCarrierListItem } from "@msuite/utils/types";
import { useFormContext, useWatch } from "react-hook-form";
import { useOrderFormContext } from "../context";

/** Props Interface */
interface OrderFormLoadCarriersHintInputProps {
	item: LoadCarrierListItem;
}

/**
 * Whether this line asks for more empties than the yard can still promise.
 *
 * The available figure already accounts for the carriers unfinished picking rounds have claimed, so
 * a yard of 242 with 20 promised to a round in flight offers 222 — and `insertOrder` refuses the
 * order outright if the line goes past it. Unlike the article hint there is no warning for a line
 * left at zero: most carriers on this list are not being ordered, and flagging each one would put
 * an alert beside every row.
 */
export function OrderFormLoadCarriersHintInput({ item }: OrderFormLoadCarriersHintInputProps) {
	/** Context */
	const { getLoadCarrierIndex } = useOrderFormContext();
	const itemIndex = getLoadCarrierIndex(item.id);

	/** Hooks */
	const { t } = useTranslation();
	const methods = useFormContext<OrderFormSchema>();
	const currentQuantity = useWatch({
		control: methods.control,
		name: `loadCarriers.${itemIndex}.quantity`,
	});

	/** Computed */
	const quantity = guardNumber(currentQuantity);
	const availableQuantity = guardNumber(item.availableQuantity);
	const isOverMaxQuantity = quantity > availableQuantity;

	/** Guard */
	if (itemIndex < 0) return null;

	/** Render */
	return (
		<div className="h-stack justify-end pr-4">
			{isOverMaxQuantity && (
				<div className="h-stack">
					<p>
						{t("stock.quantity_is_over_orderable_quantity", {
							available: formatNumber(Math.max(0, availableQuantity), "integer", 0),
						})}
					</p>
					<AlertIcon variant="danger" />
				</div>
			)}
		</div>
	);
}
